import React, { forwardRef } from 'react';
import {
  Checkbox as MuiCheckbox,
  CheckboxProps,
  MenuItem as MuiMenuItem,
  MenuItemProps,
  Radio as MuiRadio,
  RadioProps,
  Switch as MuiSwitch,
  SwitchProps,
  useFormControl,
} from '@mui/material';

/**
 * MUI v4 rendered `aria-disabled="true"` or `"false"` on every radio, checkbox, switch and menu item; v5 only adds it
 * when the control is disabled. Many of our Datadog synthetic tests were recorded on v4 and find these controls by
 * `aria-disabled="false"`, so these drop-in replacements keep the attribute. It is valid ARIA and changes nothing else.
 * Import these instead of the `@mui/material` versions.
 */
const useAriaDisabled = (disabled?: boolean): 'true' | 'false' => {
  const formControl = useFormControl();
  return (disabled ?? formControl?.disabled) ? 'true' : 'false';
};

export const Radio = forwardRef<HTMLButtonElement, RadioProps>(function Radio(props, ref) {
  return <MuiRadio aria-disabled={useAriaDisabled(props.disabled)} {...props} ref={ref} />;
});

export const Checkbox = forwardRef<HTMLButtonElement, CheckboxProps>(function Checkbox(props, ref) {
  return <MuiCheckbox aria-disabled={useAriaDisabled(props.disabled)} {...props} ref={ref} />;
});

export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(function Switch(props, ref) {
  return <MuiSwitch aria-disabled={useAriaDisabled(props.disabled)} {...props} ref={ref} />;
});

export const MenuItem = forwardRef<HTMLLIElement, MenuItemProps>(function MenuItem(props, ref) {
  return <MuiMenuItem aria-disabled={props.disabled ? 'true' : 'false'} {...props} ref={ref} />;
});
